import React, { useState } from "react";
import SideChatMessage from "./SideChatMessage";
import { Divider, TextField, Typography, CircularProgress, Tooltip, IconButton } from "@mui/material";
import { LockKeyhole, Search, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { chatService } from "../../api/chat";
import { useAuth } from "../../contexts/AuthContext";
import NewChatModal from "./NewChatModal";

const ChatSidebar = ({ setUser }) => {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['conversationList'],
    queryFn: () => chatService.getSidePanelList()
  });

  const chats = data?.results || data || [];

  return (
    <>
      <div className="w-1/3 h-full flex flex-col border-r border-gray-400">
        {/* Top bar with search + new chat FAB */}
        <div className="flex items-center gap-2 px-2 py-1 m-2">
          <div
            className="flex items-center gap-2 px-2 py-1 flex-1
                    border border-gray-300 rounded-3xl
                    focus-within:border-blue-500
                    focus-within:ring-2 focus-within:ring-blue-100
                    transition-all"
          >
            <Search className="text-blue-500 shrink-0" size={18} />
            <TextField
              placeholder="Search..."
              size="small"
              variant="outlined"
              className="flex-1"
              slotProps={{
                input: {
                  sx: {
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                },
              }}
            />
          </div>

          {/* WhatsApp-style new chat button */}
          {user?.role === "recruiter" && (
            <Tooltip title="New conversation" placement="bottom">
              <IconButton
                onClick={() => setModalOpen(true)}
                sx={{
                  backgroundColor: "#2563eb",
                  color: "white",
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  "&:hover": { backgroundColor: "#1d4ed8" },
                  transition: "background-color 0.2s ease",
                }}
              >
                <Plus size={17} />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div className="flex flex-col flex-1 pt-2 overflow-y-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {isLoading && <div className="flex justify-center p-4"><CircularProgress size={24} /></div>}
          {isError && <Typography color="error" className="p-4 text-center">Failed to load chats</Typography>}
          {!isLoading && !isError && chats.length === 0 && (
            <div className="flex flex-col items-center gap-3 mt-16 text-gray-400 px-6 text-center">
              <Plus size={36} className="opacity-40" />
              <Typography variant="body2">
                No conversations yet.<br />
                {user?.role === "recruiter" ? (
                  <>Tap the <b>+</b> button above to start one.</>
                ) : (
                  "When recruiters initiate a chat with you, it will appear here."
                )}
              </Typography>
            </div>
          )}
          {chats.map((chat) => {
            const otherUser = chat.recruiter?.id === user?.id ? chat.professional : chat.recruiter;
            if (!otherUser) return null;

            return (
              <div key={chat.id} className="px-1">
                <SideChatMessage
                  latestMessageTime={chat.updated_at}
                  sender={otherUser}
                  unread={0}
                  message={"..."}
                  setUser={setUser}
                  chat={chat}
                />
              </div>
            );
          })}
        </div>

        <Divider />
        <div className="flex gap-1 justify-center items-center p-2">
          <LockKeyhole size={11} />
          <Typography variant="caption" fontSize={11}>Your personal messages are <b>end-to-end encrypted</b></Typography>
        </div>
      </div>

      <NewChatModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onChatReady={setUser}
      />
    </>
  );
};

export default ChatSidebar;

